"use client"

import type React from "react"
import { useState, useEffect, useMemo, useRef } from "react"
import { Input } from "./ui/input"
import { Button } from "./ui/button"
import { Label } from "./ui/label"
import { Calendar } from "./ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { ModalSearchableSelect } from "./ui/modal-searchable-select"
import { CalendarIcon, Search, Briefcase, Loader2, Target } from "lucide-react"
import { format } from "date-fns"
import { cn } from "./lib/utils"
import { createApproach, createCompanyApproach } from "../services/approachesapi"
import { getJobs, getJobsForSelection } from "../services/jobsapi"
import { DayPicker } from "react-day-picker"
import "react-day-picker/dist/style.css"
import { useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast"

const sourceOptions = [
  { label: "EarlyJobs", value: "EarlyJobs" },
  { label: "LinkedIn Jobs", value: "LinkedIn Jobs" },
  { label: "Apna.co", value: "Apna.co" },
  { label: "Naukri.com", value: "Naukri.com" },
  { label: "Jobhai.com", value: "Jobhai.com" },
  { label: "Indeed India", value: "Indeed India" }, 
  { label: "Foundit", value: "Foundit" },
  { label: "Shine.com", value: "Shine.com" },
  { label: "TimesJobs", value: "TimesJobs" },
  { label: "Glassdoor", value: "Glassdoor" },
  { label: "Google Jobs", value: "Google Jobs" },
  { label: "Freshersworld", value: "Freshersworld" },
  { label: "Cutshort", value: "Cutshort" },
  { label: "Hirect", value: "Hirect" },
  { label: "Hirist", value: "Hirist" },
  { label: "LetsIntern", value: "LetsIntern" },
  { label: "JobsForHer", value: "JobsForHer" },
  { label: "WorkIndia", value: "WorkIndia" },
  { label: "Wellfound", value: "Wellfound" },
  { label: "QuikrJobs", value: "QuikrJobs" },
  { label: "Jobsora", value: "Jobsora" },
  { label: "WisdomJobs", value: "WisdomJobs" },
  { label: "Jagran Josh Jobs", value: "Jagran Josh Jobs" },
  { label: "CareerIndia", value: "CareerIndia" },
  { label: "CareerAge", value: "CareerAge" },
  { label: "Neuvoo / Talent.com", value: "Neuvoo / Talent.com" },
  { label: "Recruit.net", value: "Recruit.net" },
  { label: "WhatJobs.com", value: "WhatJobs.com" },
  { label: "JobSquare", value: "JobSquare" },
  { label: "SarkariNaukri.com", value: "SarkariNaukri.com" },
  { label: "FreeJobAlert.com", value: "FreeJobAlert.com" },
  { label: "RozgarIndia", value: "RozgarIndia" },
  { label: "JobGranth", value: "JobGranth" },
  { label: "NaukriHub", value: "NaukriHub" },
  { label: "MonsterGulf", value: "MonsterGulf" },
  { label: "CareerJet India", value: "CareerJet India" },
  { label: "JobSarkari", value: "JobSarkari" },
  { label: "Times Ascent", value: "Times Ascent" },
  { label: "Updazz", value: "Updazz" },
  { label: "Instahyre", value: "Instahyre" },
  { label: "PlacementIndia", value: "PlacementIndia" },
  { label: "SarkariResult.com", value: "SarkariResult.com" },
  { label: "FreshersLive", value: "FreshersLive" },
  { label: "JobStreet India", value: "JobStreet India" },
  { label: "PlacementIndia.com", value: "PlacementIndia.com" },
  { label: "Youth4Work", value: "Youth4Work" },
  { label: "JobNukkad", value: "JobNukkad" },
  { label: "TeamLease Jobs", value: "TeamLease Jobs" },
  { label: "Babajob", value: "Babajob" },
  { label: "Jooble India", value: "Jooble India" },
  { label: "ClickIndia Jobs", value: "ClickIndia Jobs" },
  { label: "Olx Jobs", value: "Olx Jobs" },
  { label: "Internshala", value: "Internshala" },
  { label: "Twenty19", value: "Twenty19" },
  { label: "KillerLaunch", value: "KillerLaunch" },
  { label: "Placement Season", value: "Placement Season" },
  { label: "HelloIntern", value: "HelloIntern" },
  { label: "Edugorilla Jobs", value: "Edugorilla Jobs" },
  { label: "RecruitNXT", value: "RecruitNXT" },
  { label: "FirstNaukri", value: "FirstNaukri" },
  { label: "JobBuzz", value: "JobBuzz" },
  { label: "Peeljobs", value: "Peeljobs" },
  { label: "MyJobStreet", value: "MyJobStreet" },
  { label: "DreamStarts", value: "DreamStarts" },
  { label: "BigShyft", value: "BigShyft" },
  { label: "iimjobs", value: "iimjobs" },
  { label: "Engineeristic", value: "Engineeristic" },
  { label: "PharmaTutor Jobs", value: "PharmaTutor Jobs" },
  { label: "MedJobsIndia", value: "MedJobsIndia" },
  { label: "BioJobIndia", value: "BioJobIndia" },
  { label: "CXOJobs", value: "CXOJobs" },
  { label: "JobSahaayata", value: "JobSahaayata" },
  { label: "WorkIndia.in", value: "WorkIndia.in" },
  { label: "QuickJobs", value: "QuickJobs" },
  { label: "Youth4Work Jobs", value: "Youth4Work Jobs" },
  { label: "Rozgar Bazaar", value: "Rozgar Bazaar" },
  { label: "MahaJobs (Maharashtra Govt.)", value: "MahaJobs (Maharashtra Govt.)" },
  { label: "TNVelaiPortal (Tamil Nadu Govt.)", value: "TNVelaiPortal (Tamil Nadu Govt.)" },
  { label: "Kerala PSC Jobs Portal", value: "Kerala PSC Jobs Portal" },
  { label: "MP Rojgar Portal", value: "MP Rojgar Portal" },
  { label: "Haryana Rojgar Portal", value: "Haryana Rojgar Portal" },
  { label: "UP Rojgar Sangam", value: "UP Rojgar Sangam" },
  { label: "SkillConnect Portal (Karnataka Govt.)", value: "SkillConnect Portal (Karnataka Govt.)" },
  { label: "Punjab Ghar Ghar Rozgar", value: "Punjab Ghar Ghar Rozgar" },
  { label: "Himachal Rojgar Portal", value: "Himachal Rojgar Portal" },
  { label: "Chhattisgarh Rojgar Portal", value: "Chhattisgarh Rojgar Portal" },
  { label: "Odisha Employment Portal", value: "Odisha Employment Portal" },
  { label: "Assam Career", value: "Assam Career" },
  { label: "SarkariExam.com", value: "SarkariExam.com" },
  { label: "SarkariJobFind.com", value: "SarkariJobFind.com" },
  { label: "EmploymentNews.gov.in", value: "EmploymentNews.gov.in" },
  { label: "JobMela.in", value: "JobMela.in" },
  { label: "Rocket.in", value: "Rocket.in" },
  { label: "GigIndia", value: "GigIndia" },
  { label: "FlexingIt", value: "FlexingIt" },
  { label: "Truelancer", value: "Truelancer" },
  { label: "WorkNHire", value: "WorkNHire" },
  { label: "Freelancer.in", value: "Freelancer.in" },
  { label: "Upwork India", value: "Upwork India" },
  { label: "Fiverr India", value: "Fiverr India" },
  { label: "Others", value: "Others" }
]


const statusOptions = [
  { label: "Call Back", value: "Call Back" },
  {label:"Interested",value:"Interested"},
  { label: "Ringing Not Reachable", value: "Ringing Not Reachable" },
  { label: "Not Relevant", value: "Not Relevant" },
  { label: "Not Reachable", value: "Not Reachable" },
  { label: "Not Interested", value: "Not Interested" },
  { label: "DND", value: "DND" },
]

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd?: (user: any) => boolean | Promise<boolean>
}

const AddCompanyModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [form, setForm] = useState({
    brandName: "",
    legalName: "",
    number: "",
    email: "",
    city: "",
    source: "",
    status: "",
    date: "",
    time: "",
    remarks: "",
  })
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const [jobs, setJobs] = useState<{  _id: string; title: string; companyName?: string; location?: string }[]>([])
  const [jobLoading, setJobLoading] = useState(false)
  const [jobsError, setJobsError] = useState<string | null>(null)
  const [jobSearchTerm, setJobSearchTerm] = useState("")
  const[jobId,setJobId]= useState("")
  const [isJobSelectOpen, setIsJobSelectOpen] = useState(false)
  const jobSearchInputRef = useRef<HTMLInputElement>(null)

  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen) {
      setJobLoading(true)
      setJobsError(null)
      getJobs()
        .then((data) => {
          const jobsData =
            data.data.jobs?.map((job: any) => ({
              _id: job.id,
              title: job.title,
              companyName: job.companyName,
              location: job.location,
            })) || []
          setJobs(jobsData)
        })
        .catch((error) => {
          console.error("Failed to fetch jobs:", error)
          setJobsError("Failed to load job positions")
          setJobs([])
        })
        .finally(() => setJobLoading(false))
    } else {
      // Reset job search state when modal closes
      setJobSearchTerm("")
      setIsJobSelectOpen(false)
    }
  }, [isOpen])
useEffect(() => {
  // Debounce the search to prevent losing focus on every keystroke
  const timeoutId = setTimeout(() => {
    const filteredJobs = async () => {
      // Store if the input was focused before the API call
      const wasInputFocused = document.activeElement === jobSearchInputRef.current
      
      try {
        const response = await getJobsForSelection({ search: jobSearchTerm })
        setJobs(response.data.jobs || [])
        
        // Restore focus after state update if it was focused before
        if (wasInputFocused && jobSearchInputRef.current) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              jobSearchInputRef.current?.focus()
            }, 0)
          })
        }
      } catch (err) {
        console.error("Failed to fetch filtered jobs:", err)
        // Restore focus even on error
        if (wasInputFocused && jobSearchInputRef.current) {
          requestAnimationFrame(() => {
            jobSearchInputRef.current?.focus()
          })
        }
      }
    }
    filteredJobs()
  }, 300) // 300ms debounce delay

  return () => clearTimeout(timeoutId)
}, [jobSearchTerm])

  // Focus the search input when the select opens
  useEffect(() => {
    if (isJobSelectOpen && jobSearchInputRef.current) {
      // Small delay to ensure the SelectContent is fully rendered
      setTimeout(() => {
        jobSearchInputRef.current?.focus()
      }, 100)
    }
  }, [isJobSelectOpen])

  // Restore focus after jobs state updates (when API response comes back)
  useEffect(() => {
    if (isJobSelectOpen && jobSearchInputRef.current && jobSearchTerm.length > 0) {
      // Restore focus after jobs update if user was typing
      // Use requestAnimationFrame to ensure DOM is updated after state change
      requestAnimationFrame(() => {
        setTimeout(() => {
          jobSearchInputRef.current?.focus()
        }, 0)
      })
    }
  }, [jobs, isJobSelectOpen, jobSearchTerm])

  // const filteredJobs = useMemo(() => {
  //   if (!jobSearchTerm.trim()) return jobs

  //   return jobs.filter((job) => {
  //     const searchLower = jobSearchTerm.toLowerCase()
  //     return (
  //       job.title?.toLowerCase().includes(searchLower) ||
  //       job.companyName?.toLowerCase().includes(searchLower) ||
  //       job.location?.toLowerCase().includes(searchLower)
  //     )
  //   })
  // }, [jobs, jobSearchTerm])



const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  setLoading(true)
  try {
    const payload: any = {
      brandName: form.brandName,
      legalName: form.legalName,
      phone: form.number,
      email: form.email,
      city: form.city,
      source: form.source,
      callStatus: form.status,
      remarks: form.remarks,
    }

   

    if (form.status === "Call Back") {
      payload.callBackTime = `${form.date}T${form.time}`
    }

    // Create approach immediately for non-schedule statuses
    await createCompanyApproach(payload)

    setForm({
      brandName: "",
      legalName: "",
      number: "",
      email: "",
      city: "",
      source: "",
      status: "",
      date: "",
      time: "",
      remarks: "",
    })
    setJobId("")
    setCalendarDate(undefined)
    setJobSearchTerm("")
    onClose()
  } catch (error) {
    console.error("Error adding user:", error)
   
  } finally {
    setLoading(false)
  }
}

  const handleStatusChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      status: value,
      date: "",
      time: "",
    }))
    setCalendarDate(undefined)
  }

  const handleDateSelect = (date: Date | undefined) => {
    setCalendarDate(date)
    setForm((prev) => ({
      ...prev,
      date: date ? format(date, "yyyy-MM-dd") : "",
    }))
    setIsCalendarOpen(false)
  }

  const isCallback = form.status === "Call Back"
  const isFormValid =
    form.brandName &&
    form.number &&
    form.source &&
    form.status &&
    // form.jobTitle &&
    (!isCallback || (form.date && form.time))

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl font-semibold text-center">Add New Company</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
           {/* <div className="space-y-3 max-w-full">
              <Label htmlFor="jobId" className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-500" />
                Job Position
              </Label>
              <div className="relative w-full">
                <Select
                  value={form.jobTitle}
                  open={isJobSelectOpen}
                  onOpenChange={setIsJobSelectOpen}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, jobTitle: value, }))
                    console.log(value)
                    const selectedJob = jobs.find((job) => job._id === value)
                    console.log("selectedJob:", jobs)
                    if (selectedJob) {
                      setJobId(selectedJob._id)
                      console.log("Selected job:", selectedJob)
                    }
                    setIsJobSelectOpen(false)
                  }}
                  disabled={jobLoading}
                >
                  <SelectTrigger className="h-11 w-full rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/70 font-medium shadow-sm hover:shadow-md transition-all duration-200">
                    <SelectValue
                      placeholder={
                        jobLoading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading available positions...</span>
                          </div>
                        ) : jobsError ? (
                          "Error loading jobs - Click to retry"
                        ) : jobs.length === 0 ? (
                          "No positions available"
                        ) : (
                          "Search and select a job position"
                        )
                      }
                    />
                  </SelectTrigger>
                  <SelectContent 
                    className="rounded-xl max-h-80 border-2 shadow-2xl w-[var(--radix-select-trigger-width)]"
                    onCloseAutoFocus={(e) => {
                      // Prevent closing when clicking inside the content
                      e.preventDefault()
                    }}
                  >
                    <div className="p-3 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          ref={jobSearchInputRef}
                          placeholder="Search jobs by title, company, or location..."
                          value={jobSearchTerm}
                          onChange={(e) => {
                            setJobSearchTerm(e.target.value)
                            // Keep the select open while typing
                            setIsJobSelectOpen(true)
                          }}
                          onClick={(e) => {
                            // Prevent click from closing the select
                            e.stopPropagation()
                          }}
                          onKeyDown={(e) => {
                            // Prevent Enter key from closing the select
                            if (e.key === 'Enter') {
                              e.preventDefault()
                            }
                          }}
                          className="pl-10 h-10 rounded-lg border-gray-200 focus:border-orange-400 focus:ring-orange-400"
                        />
                      </div>
                    </div>

                    {jobLoading ? (
                      <div className="flex items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                          <span className="text-sm font-medium text-gray-600">Loading positions...</span>
                        </div>
                      </div>
                    ) : jobsError ? (
                      <div className="p-6 text-center">
                        <div className="flex flex-col gap-3">
                          <div className="text-red-600 font-medium">{jobsError}</div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            className="self-center bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                          >
                            🔄 Retry Loading
                          </Button>
                        </div>
                      </div>
                    ) : !Array.isArray(jobs) || jobs.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <Briefcase className="h-8 w-8 text-gray-300" />
                          <span className="font-medium">
                            {!Array.isArray(jobs) ? "Invalid job data format" : "No positions available"}
                          </span>
                        </div>
                      </div>
                    ) : jobs.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <Search className="h-8 w-8 text-gray-300" />
                          <span className="font-medium">No jobs match your search</span>
                          <span className="text-sm">Try different keywords</span>
                        </div>
                      </div>
                    ) : (
                      jobs
                        .map((job, index) => {
                          if (!job) {
                            console.warn("Invalid job object:", job)
                            return null
                          }

                          const jobId = job._id || `job-${index}`
                          const jobTitle = job.title || "Untitled Job"
                          const displayText = job.companyName ? `${jobTitle} - ${job.companyName}` : jobTitle

                          return (
                            <SelectItem
                              key={jobId}
                              value={jobId}
                              className="cursor-pointer hover:bg-orange-50 focus:bg-orange-50"
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-medium">{displayText}</span>
                                {job.location && <span className="text-xs text-gray-500">{job.location}</span>}
                              </div>
                            </SelectItem>
                          )
                        })
                        .filter(Boolean)
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="brandName" className="text-sm font-medium text-gray-700">
                Brand Name  <span className="text-red-500">*</span>
              </Label>
              <Input
                id="brandName"
                placeholder="Enter brand name"
                value={form.brandName}
                onChange={(e) => setForm((prev) => ({ ...prev, brandName: e.target.value }))}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="legalName" className="text-sm font-medium text-gray-700">
                Legal Name
              </Label>
              <Input
                id="legalName"
                placeholder="Enter legal name"
                value={form.legalName}
                onChange={(e) => setForm((prev) => ({ ...prev, legalName: e.target.value }))}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="mobileNumber"
                type="tel"
                placeholder="Enter phone number"
                value={form.number}
                onChange={(e) => setForm((prev) => ({ ...prev, number: e.target.value }))}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                City
              </Label>
              <Input
                id="city"
                placeholder="Enter city"
                value={form.city}
                onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="source" className="text-sm font-medium text-gray-700">
                Source <span className="text-red-500">*</span>
              </Label>
              <Input
                id="source"
                type="text"
                placeholder="Enter source"
                value={form.source}
                onChange={(e) => setForm((prev) => ({ ...prev, source: e.target.value }))}
                className="h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select value={form.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

           
          </div>
         
               <div className="space-y-2">
              <Label htmlFor="remarks" className="text-sm font-medium text-gray-700">
                Remarks
              </Label>
              <Input
                id="remarks"
                placeholder="Enter Remarks"
                value={form.remarks}
                onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                className="h-11"
                
              />
            </div>
            
          
          {isCallback && (
            <div className="space-y-4 p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-orange-600" />
                Callback Date & Time
              </h3>

              {/* Show selected date and time for Call Back */}
              {form.date && form.time && (
                <div className="text-sm text-orange-700 font-medium mb-2">
                  Selected: {format(new Date(form.date), "dd MMM yyyy")} at {form.time}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl shadow p-2">
                  <DayPicker
                    mode="single"
                    selected={calendarDate}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))}
                    className="h-11"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="h-11 px-6 bg-transparent">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || loading}
              className="h-11 px-6 bg-[#EC6E51] hover:bg-[#d65f47] text-white disabled:opacity-50"
            >
              {loading ? "Adding..." : "Add Company"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddCompanyModal